/**
 * Tier picker rendered on BillingTab when the org has no active subscription.
 *
 * Layout principles (matching the Paygentic billing-tab design):
 *   - One card per tier; no shared bottom summary bar.
 *   - Self-serve cards (Startup) own their cadence toggle + seat stepper +
 *     Subscribe button. Cadence selection is local to the card so the toggle
 *     is clearly anchored to the tier it controls.
 *   - Sales-led cards (Pro, Growth) render a static "Yearly" / "Custom"
 *     label in the same vertical slot a self-serve cadence toggle occupies,
 *     so the price block beneath sits at the same y across all three cards.
 *   - "Most popular" is signaled by border colour only — no pill, no ribbon.
 *   - Each card's CTA pins to the bottom via `mt-auto`, keeping the three
 *     CTA buttons row-aligned even when feature lists have different lengths.
 *
 * Self-serve POSTs `{tier, cadence, seat_count}` to /billing/checkout-session/.
 * Sales-led opens `tier.sales_url` (falling back to a build-time constant)
 * in a new tab.
 */

import { Check, Minus, Plus } from '@untitledui/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { formatMoney } from '@/lib/billing/currency';
import type {
  BillingCadence,
  BillingPlan,
  BillingPlanPrice,
  CheckoutSessionOptions,
} from '@/lib/billing/types';
import { cx } from '@/utils/cx';

const MIN_SEATS = 1;
const MAX_SEATS = 1000;
// External fallback for tiers whose ``sales_url`` is empty (defensive — the
// backend always provides a value when self_serve=false).
const SALES_URL_FALLBACK = 'https://meet.ahoy.ai/ahoy-team';

// Shared className for the ± buttons in `SeatStepper`. Module-scope so both
// buttons reference the same string (stable identity, easy to audit).
//
// Key design constraints:
//   - `h-6 w-6` locks dimensions; `box-sizing: border-box` (Tailwind default)
//     keeps the 1px border inside the 24×24 box. Dimensions never change.
//   - `focus:outline-none` suppresses the browser's default `:focus` outline.
//     The default outline renders OUTSIDE the box (2-4px), which visually
//     reads as the button growing on click. That's the bug we're fixing.
//   - `focus-visible:ring-* ring-inset` gives KEYBOARD users a clear focus
//     indicator without expanding the box. We pair this with the
//     `STEPPER_PREVENT_MOUSE_FOCUS` mousedown handler below — mouse clicks
//     never receive focus at all, so even browsers with looser
//     `focus-visible` heuristics (Safari) won't show a ring after a click.
//   - `transition-colors` softens the hover/disabled background transition so
//     state changes feel like a fade, not a flash.
const STEPPER_BUTTON_CLASS =
  'inline-flex h-6 w-6 items-center justify-center rounded-md border border-[var(--color-border-secondary)] text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-bg-secondary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-brand-600)] disabled:opacity-50';

// `preventDefault` on mousedown stops the browser's default behaviour of
// focusing the target. The subsequent click event still fires, so onClick
// runs normally — the button just never becomes `document.activeElement`,
// which means no `:focus` / `:focus-visible` pseudo-class matches. Keyboard
// users (Tab) still focus normally because Tab doesn't go through mousedown.
function preventMouseFocus(event: React.MouseEvent<HTMLButtonElement>) {
  event.preventDefault();
}

interface PlanPickerSectionProps {
  plans: BillingPlan[];
  /**
   * Called when the user clicks "Subscribe" on a self-serve card — opens
   * Stripe Checkout in the current tab. Resolves once Stripe returns the
   * redirect URL and the navigation has been issued.
   */
  onSubscribe: (options: CheckoutSessionOptions) => Promise<void>;
  isLoading?: boolean;
}

function findPrice(
  plan: BillingPlan,
  cadence: BillingCadence
): BillingPlanPrice | undefined {
  return plan.prices.find(p => p.cadence === cadence);
}

/**
 * Always-render a per-seat-per-month price string for the given cadence.
 * Yearly prices are divided by 12 so the customer sees an apples-to-apples
 * monthly comparison; the "Billed annually" subtitle communicates the
 * billing frequency separately.
 */
function formatPerSeatMonthlyPrice(price: BillingPlanPrice): string {
  const monthlyEquivalentCents =
    price.cadence === 'yearly'
      ? Math.round(price.per_seat_cents / 12)
      : price.per_seat_cents;
  return formatMoney(monthlyEquivalentCents, price.currency);
}

interface CadenceToggleProps {
  cadence: BillingCadence;
  onChange: (cadence: BillingCadence) => void;
  disabled?: boolean;
  /** When > 0, renders a "Save N%" badge next to the Yearly option. */
  yearlyDiscountPercent?: number;
}

function CadenceToggle({
  cadence,
  onChange,
  disabled,
  yearlyDiscountPercent = 0,
}: CadenceToggleProps) {
  const setMonthly = useCallback(() => onChange('monthly'), [onChange]);
  const setYearly = useCallback(() => onChange('yearly'), [onChange]);

  return (
    <div
      role='radiogroup'
      aria-label='Billing cadence'
      className='mt-4 grid w-full grid-cols-2 rounded-lg border border-[var(--color-border-secondary)] bg-[var(--color-bg-secondary)] p-0.5'
    >
      <button
        type='button'
        role='radio'
        aria-checked={cadence === 'monthly'}
        disabled={disabled}
        onClick={setMonthly}
        className={cx(
          'rounded-md py-1 text-xs font-medium transition-colors disabled:opacity-50',
          cadence === 'monthly'
            ? 'bg-[var(--color-bg-primary)] text-[var(--color-text-primary)] shadow-sm'
            : 'text-[var(--color-text-tertiary)] hover:text-[var(--color-text-secondary)]'
        )}
      >
        Monthly
      </button>
      <button
        type='button'
        role='radio'
        aria-checked={cadence === 'yearly'}
        disabled={disabled}
        onClick={setYearly}
        className={cx(
          'inline-flex items-center justify-center gap-1.5 rounded-md py-1 text-xs font-medium transition-colors disabled:opacity-50',
          cadence === 'yearly'
            ? 'bg-[var(--color-bg-primary)] text-[var(--color-text-primary)] shadow-sm'
            : 'text-[var(--color-text-tertiary)] hover:text-[var(--color-text-secondary)]'
        )}
      >
        Yearly
        {yearlyDiscountPercent > 0 && (
          <span className='rounded-full bg-[var(--color-brand-600)] px-1.5 py-0.5 text-[10px] font-semibold leading-none text-white'>
            Save {yearlyDiscountPercent}%
          </span>
        )}
      </button>
    </div>
  );
}

interface CadenceLabelProps {
  label: string;
}

/**
 * Static cadence indicator rendered on sales-led tiers. Same vertical
 * footprint as `CadenceToggle` so the price blocks align across cards.
 */
function CadenceLabel({ label }: CadenceLabelProps) {
  return (
    <div
      aria-hidden='true'
      className='mt-4 flex h-8 items-center text-xs font-medium uppercase tracking-wide text-[var(--color-text-tertiary)]'
    >
      {label}
    </div>
  );
}

interface SeatStepperProps {
  value: number;
  min?: number;
  max?: number;
  onChange: (value: number) => void;
  ariaLabel?: string;
  disabled?: boolean;
  /**
   * Layout mode. Default `false` = full-width inside its parent with
   * `justify-between` (used in the PlanPicker card so the label hugs the
   * left edge and the controls hug the right). Set to `true` for inline
   * usage (e.g. next to an "Update" button in `SubscriptionSection`) so
   * the stepper sizes to its content and doesn't stretch a flex parent
   * into a wide empty span between the label and the controls.
   */
  compact?: boolean;
}

export function SeatStepper({
  value,
  min = MIN_SEATS,
  max = MAX_SEATS,
  onChange,
  ariaLabel = 'Seat count',
  disabled,
  compact = false,
}: SeatStepperProps) {
  const [inputValue, setInputValue] = useState(String(value));
  useEffect(() => {
    setInputValue(String(value));
  }, [value]);

  const handleDecrement = useCallback(() => {
    onChange(Math.max(min, value - 1));
  }, [onChange, value, min]);

  const handleIncrement = useCallback(() => {
    onChange(Math.min(max, value + 1));
  }, [onChange, value, max]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      setInputValue(raw);
      const next = Number.parseInt(raw, 10);
      if (Number.isFinite(next)) {
        onChange(Math.min(max, Math.max(min, next)));
      }
    },
    [onChange, min, max]
  );

  const handleInputBlur = useCallback(() => {
    setInputValue(String(value));
  }, [value]);

  return (
    <div
      role='group'
      className={cx(
        'rounded-lg border border-[var(--color-border-secondary)] bg-[var(--color-bg-primary)] px-3 py-1.5',
        // Compact (SubscriptionSection inline use):
        //   - `flex w-fit` → element width = sum of children + padding + gaps,
        //     never grows beyond content. Explicit `w-fit` is more robust than
        //     relying on `inline-flex`'s implicit content-sizing (which gets
        //     blockified to `display: flex` when nested in a flex parent and
        //     can behave unexpectedly).
        //   - `shrink-0` → never shrinks below content size either, regardless
        //     of how much room the parent flex container has.
        //   - `gap-3` → 12px between "Seats" label and the ±/input group.
        // Non-compact (PlanPicker card use):
        //   - Spans the card's full width, label hard-left, controls hard-right.
        compact
          ? 'flex w-fit shrink-0 items-center gap-3 whitespace-nowrap'
          : 'flex items-center justify-between'
      )}
      aria-label={ariaLabel}
    >
      <span className='text-xs font-medium text-[var(--color-text-secondary)]'>
        Seats
      </span>
      <div className='flex items-center gap-1'>
        <button
          type='button'
          onClick={handleDecrement}
          onMouseDown={preventMouseFocus}
          aria-label='Decrease seats'
          disabled={disabled || value <= min}
          className={STEPPER_BUTTON_CLASS}
        >
          <Minus className='h-3 w-3' />
        </button>
        <input
          type='number'
          inputMode='numeric'
          value={inputValue}
          min={min}
          max={max}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          disabled={disabled}
          aria-label={`${ariaLabel} value`}
          // `ring-inset` keeps the focus indicator inside the box so the
          // input doesn't visually expand by 1px when clicked. Same rationale
          // as STEPPER_BUTTON_CLASS above.
          className='w-12 rounded-md border border-[var(--color-border-secondary)] bg-[var(--color-bg-primary)] px-1.5 py-0.5 text-center text-xs font-medium tabular-nums text-[var(--color-text-primary)] focus:outline-none focus:ring-1 focus:ring-inset focus:ring-[var(--color-brand-600)] disabled:opacity-50'
        />
        <button
          type='button'
          onClick={handleIncrement}
          onMouseDown={preventMouseFocus}
          aria-label='Increase seats'
          disabled={disabled || value >= max}
          className={STEPPER_BUTTON_CLASS}
        >
          <Plus className='h-3 w-3' />
        </button>
      </div>
    </div>
  );
}

interface PriceBlockProps {
  amount: string;
  subtitle: string;
}

/**
 * Shared price block — big number + "per seat, per month" subtitle on its
 * own line + a billing subtitle below. Used by both self-serve and sales-led
 * cards so the three tiers line up visually. `amount` may be a currency
 * string (`"$99"`, `"$79"`) or a marketing word (`"Custom"`).
 */
function PriceBlock({ amount, subtitle }: PriceBlockProps) {
  // Custom-quote rendering — no "per seat, per month" suffix because there's
  // no unit price. We render the line `invisible` (rather than omit) so the
  // billing subtitle below sits at the same vertical position as the priced
  // tiers' subtitle, keeping the three cards aligned row-by-row. Compare
  // against the known non-priced sentinel so any currency formatMoney emits
  // (USD, EUR, GBP, JPY, …) renders with the suffix.
  const isPriced = amount !== 'Custom';
  return (
    <div className='mt-5'>
      <div className='text-3xl font-semibold leading-none tracking-tight text-[var(--color-text-primary)] sm:text-4xl'>
        {amount}
      </div>
      <p
        aria-hidden={!isPriced}
        className={cx(
          'mt-2 text-xs font-medium text-[var(--color-text-tertiary)]',
          !isPriced && 'invisible'
        )}
      >
        per seat, per month
      </p>
      <p className='mt-2 text-xs text-[var(--color-text-tertiary)]'>
        {subtitle}
      </p>
    </div>
  );
}

interface TierFeaturesProps {
  features: string[];
}

function TierFeatures({ features }: TierFeaturesProps) {
  return (
    <ul className='mt-5 space-y-2 text-sm text-[var(--color-text-secondary)]'>
      {features.map(feature => (
        <li key={feature} className='flex items-start gap-2'>
          <Check className='mt-0.5 h-4 w-4 shrink-0 text-[var(--color-text-success-primary)]' />
          <span>{feature}</span>
        </li>
      ))}
    </ul>
  );
}

interface TierCardShellProps {
  plan: BillingPlan;
  recommended: boolean;
  children: React.ReactNode;
}

/**
 * Common card chrome for all tiers. Ensures equal heights via `h-full` +
 * `flex flex-col` so children's `mt-auto` footers align CTAs across the row.
 *
 * The `recommended` prop swaps the border colour to the brand token —
 * that single visual difference is the only "Most popular" signal (no pill,
 * no ribbon).
 *
 * `min-h-[2.5rem]` on the tagline reserves space for two lines of `text-xs`
 * `leading-relaxed` (~39px) so the price block beneath aligns row-by-row
 * even when one tier's tagline is shorter than the others.
 */
function TierCardShell({ plan, recommended, children }: TierCardShellProps) {
  return (
    <div
      data-component='plan-card'
      data-instance={`select-tier-${plan.tier}`}
      data-recommended={recommended || undefined}
      className={cx(
        'relative flex h-full flex-col rounded-xl bg-[var(--color-bg-primary)] p-5 shadow-xs',
        recommended
          ? 'border border-[var(--color-brand-600)] shadow-sm'
          : 'border border-[var(--color-border-secondary)] transition-shadow hover:shadow-sm'
      )}
    >
      <h3 className='text-base font-semibold text-[var(--color-text-primary)]'>
        {plan.name}
      </h3>
      <p className='mt-1 line-clamp-2 min-h-[2.5rem] text-xs leading-relaxed text-[var(--color-text-tertiary)]'>
        {plan.description}
      </p>
      {children}
    </div>
  );
}

interface SelfServeTierCardProps {
  plan: BillingPlan;
  seatCount: number;
  onSeatCountChange: (seats: number) => void;
  onSubscribe: (options: CheckoutSessionOptions) => Promise<void>;
  loading: boolean;
}

/**
 * Self-serve tier card — interactive cadence toggle + seat stepper inside
 * the card, "Subscribe" CTA at the bottom. Cadence selection is local to
 * this card so the toggle is clearly anchored to its tier.
 */
function SelfServeTierCard({
  plan,
  seatCount,
  onSeatCountChange,
  onSubscribe,
  loading,
}: SelfServeTierCardProps) {
  // Resolve the initial cadence from the tier's `default_cadence` so the
  // cheaper yearly price shows by default. Fall back to the first available
  // cadence if `default_cadence` isn't present in the catalog.
  const initialCadence = useMemo<BillingCadence>(() => {
    const hasDefault = plan.prices.some(
      p => p.cadence === plan.default_cadence
    );
    if (hasDefault) {
      return plan.default_cadence;
    }
    return (plan.prices[0]?.cadence ?? 'monthly') as BillingCadence;
  }, [plan.prices, plan.default_cadence]);

  const [cadence, setCadence] = useState<BillingCadence>(initialCadence);

  const price = useMemo(
    () => findPrice(plan, cadence) ?? plan.prices[0],
    [plan, cadence]
  );

  const handleSubscribe = useCallback(async () => {
    if (!price) {
      return;
    }
    await onSubscribe({
      tier: plan.tier,
      cadence: price.cadence,
      seatCount,
    });
  }, [onSubscribe, plan.tier, price, seatCount]);

  const billingSubtitle =
    price?.cadence === 'yearly' ? 'Billed annually' : 'Billed monthly';
  const amount = price ? formatPerSeatMonthlyPrice(price) : '—';

  return (
    <TierCardShell plan={plan} recommended={plan.is_default}>
      {plan.prices.length > 1 ? (
        <CadenceToggle
          cadence={cadence}
          onChange={setCadence}
          disabled={loading}
          yearlyDiscountPercent={plan.yearly_discount_percent}
        />
      ) : (
        <CadenceLabel label={cadence === 'yearly' ? 'Yearly' : 'Monthly'} />
      )}
      <PriceBlock amount={amount} subtitle={billingSubtitle} />
      <TierFeatures features={plan.features} />
      <div className='mt-auto pt-5'>
        <SeatStepper
          value={seatCount}
          onChange={onSeatCountChange}
          ariaLabel={`${plan.name} seat count`}
          disabled={loading}
        />
        <Button
          color='primary'
          size='sm'
          className='mt-3 w-full'
          onClick={handleSubscribe}
          isLoading={loading}
          isDisabled={loading || !price}
        >
          {plan.cta_label || 'Subscribe'}
        </Button>
      </div>
    </TierCardShell>
  );
}

interface SalesLedTierCardProps {
  plan: BillingPlan;
}

/**
 * Sales-led tier card — static cadence label + price (or "Custom"), marketing
 * copy, and a "Request a demo" button opening `plan.sales_url` in a new tab.
 * No subscribe path — sales engages with the customer first.
 */
function SalesLedTierCard({ plan }: SalesLedTierCardProps) {
  const handleRequestDemo = useCallback(() => {
    const target = plan.sales_url || SALES_URL_FALLBACK;
    window.open(target, '_blank', 'noopener,noreferrer');
  }, [plan.sales_url]);

  // Pro has a single yearly cadence; Growth has none. The label below also
  // doubles as a small informational tag for the customer.
  const price = plan.prices[0];
  const cadenceLabel = (() => {
    if (!price) {
      return 'Custom';
    }
    return price.cadence === 'yearly' ? 'Yearly' : 'Monthly';
  })();

  const amount = price ? formatPerSeatMonthlyPrice(price) : 'Custom';
  const subtitle = price ? 'Billed annually' : 'Tailored to your team';

  return (
    <TierCardShell plan={plan} recommended={plan.is_default}>
      <CadenceLabel label={cadenceLabel} />
      <PriceBlock amount={amount} subtitle={subtitle} />
      <TierFeatures features={plan.features} />
      <div className='mt-auto pt-5'>
        <Button
          color='secondary'
          size='sm'
          className='w-full'
          onClick={handleRequestDemo}
        >
          {plan.cta_label || 'Request a demo'}
        </Button>
      </div>
    </TierCardShell>
  );
}

export function PlanPickerSection({
  plans,
  onSubscribe,
  isLoading,
}: PlanPickerSectionProps) {
  // Per-tier seat counts. Only self-serve tiers actually use this; sales-led
  // tiers don't have a seat picker. Keyed by tier slug.
  const [seatCounts, setSeatCounts] = useState<Record<string, number>>({});

  const handleSeatCountChange = useCallback(
    (tier: string) => (seats: number) => {
      setSeatCounts(prev => ({ ...prev, [tier]: seats }));
    },
    []
  );

  return (
    // `data-wide` triggers settings-layout's `has-[[data-wide]]:max-w-[960px]`
    // variant so the 3-card pricing grid gets ~285px per card instead of the
    // default 800px-cap squeeze. The attribute is here (not on the section
    // root) so SubscriptionSection / the active-subscription view stays at
    // the default narrower width.
    <div data-wide className='space-y-5'>
      <header className='flex flex-col gap-1'>
        <h2 className='text-xl font-semibold tracking-tight text-[var(--color-text-primary)]'>
          Choose your plan
        </h2>
        <p className='text-sm text-[var(--color-text-tertiary)]'>
          Pick the tier that fits your team. Startup is self-serve. Pro and
          Growth are sales-led – book a demo and we&apos;ll get you set up.
        </p>
      </header>
      <div className='grid items-stretch gap-5 lg:grid-cols-3'>
        {plans.map(plan => {
          if (plan.self_serve) {
            return (
              <SelfServeTierCard
                key={plan.tier}
                plan={plan}
                seatCount={seatCounts[plan.tier] ?? 1}
                onSeatCountChange={handleSeatCountChange(plan.tier)}
                onSubscribe={onSubscribe}
                loading={Boolean(isLoading)}
              />
            );
          }
          return <SalesLedTierCard key={plan.tier} plan={plan} />;
        })}
      </div>
    </div>
  );
}
